<template>
  <div class="flex items-center overflow-x-auto">
    <Tabs
      activationMode="manual"
      @update:modelValue="handleSelectView"
      :size="!maxSmall ? 'sm' : 'xl'"
      :orientation="!maxSmall ? 'horizontal' : 'vertical'"
      :fullWidth="true"
      :modelValue="activeTab"
      class="shrink-0 w-full"
    >
      <Tab
        v-for="view in defaultViews"
        :key="view.id"
        :label="view.name"
        :badge="view.count"
        :hasDivider="view.divider"
        :loading="loading"
        :disabled="view.disabled"
        :disabledTooltip="view.disabledTooltip"
      />
      <template #end>
        <template v-if="!maxSmall">
          <div class="flex gap-1 shrink-0">
            <Divider
              v-if="allowViewCreation || customViews.length > 0"
              orientation="vertical"
              class="py-2 ml-1 h-9"
            />
            <div class="flex items-center gap-1">
              <div v-if="!isEmpty(customViews)" class="shrink-0 flex gap-1">
                <div
                  v-for="view in customViews.slice(0, 2)"
                  :key="view.id"
                  class="cursor-pointer inline-flex items-center rounded-lg shrink-0 transition-colors font-semibold select-none focus-visible:outline-none border border-transparent justify-center h-[36px] text-sm gap-1.5 [&>svg]:h-4.5 [&>svg]:w-4.5"
                  :class="{
                    'bg-utility-brand-50_alt fg-brand-primary_alt':
                      isActive(view),
                    'hover:border-secondary text-quaternary':
                      !disableCustomView,
                    'fg-quinary': disableCustomView,
                  }"
                >
                  <button
                    v-if="!isActive(view)"
                    class="h-full flex items-center px-3"
                    :disabled="loading"
                    @click="handleSelectCustomView(view)"
                  >
                    {{ view.name }}
                  </button>
                  <Dropdown v-else align="start" sideOffset="1">
                    <div class="h-full flex gap-2 items-center pl-3 pr-1">
                      {{ view.name }}
                      <Button
                        variant="tertiary"
                        type="color"
                        size="2xs"
                        class="px-1"
                      >
                        <template #start>
                          <Icon name="chevron-down" />
                        </template>
                      </Button>
                    </div>
                    <template #content>
                      <div class="w-[240px] text-secondary p-1.5">
                        <DropdownItem
                          class="hover:bg-primary_hover"
                          :label="t('components.buttons.rename')"
                          @click="toggleRenameViewDialog(view.id)"
                        >
                          <template #start>
                            <Icon class="fg-quaternary" name="edit-pen" />
                          </template>
                        </DropdownItem>
                        <DropdownItem
                          class="hover:bg-primary_hover"
                          :label="t('components.buttons.delete')"
                          @click="toggleDeleteViewDialog(view.id)"
                        >
                          <template #start>
                            <Icon class="fg-quaternary" name="delete" />
                          </template>
                        </DropdownItem>
                      </div>
                    </template>
                  </Dropdown>
                </div>
                <Dropdown
                  :open="isDropdownOpen"
                  align="start"
                  @update:open="isDropdownOpen = $event"
                  v-if="!isEmpty(customViews.slice(2))"
                >
                  <div
                    class="cursor-pointer inline-flex items-center rounded-lg shrink-0 transition-colors font-semibold select-none focus-visible:outline-none border border-transparent justify-center h-[36px] px-3 text-sm gap-1.5 [&>svg]:h-4.5 [&>svg]:w-4.5 text-quaternary hover:border-secondary"
                  >
                    {{
                      t("components.buttons.x_more", {
                        number: customViews.slice(2).length,
                      })
                    }}
                  </div>
                  <template #content>
                    <div class="w-[240px] text-secondary p-1.5">
                      <DropdownMenuSub
                        :key="view.id"
                        v-for="view in customViews.slice(2)"
                      >
                        <DropdownMenuSubTrigger>
                          <div
                            class="cursor-pointer rounded-sm transition-colors bg-primary px-2.5 h-10 flex gap-2 items-center text-secondary text-sm font-medium"
                            @click="handleSelectCustomView(view)"
                          >
                            <div class="grow">
                              {{ view.name }}
                            </div>
                            <Icon
                              name="chevron-right"
                              class="button-tertiary-gray-fg"
                            />
                          </div>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                          <DropdownMenuSubContent
                            sideOffset="1"
                            class="w-[240px] p-1.5 shadow-lg bg-primary rounded-md border border-secondary z-[100]"
                          >
                            <DropdownMenuItem>
                              <DropdownItem
                                class="hover:bg-primary_hover"
                                :label="t('components.buttons.rename')"
                                @click="toggleRenameViewDialog(view.id)"
                              >
                                <template #start>
                                  <Icon class="fg-quaternary" name="edit-pen" />
                                </template>
                              </DropdownItem>
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <DropdownItem
                                class="hover:bg-primary_hover"
                                :label="t('components.buttons.delete')"
                                @click="toggleDeleteViewDialog(view.id)"
                              >
                                <template #start>
                                  <Icon class="fg-quaternary" name="delete" />
                                </template>
                              </DropdownItem>
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                      </DropdownMenuSub>

                      <template v-if="allowViewCreation">
                        <Divider orientation="horizontal" class="w-full py-1" />
                        <DropdownItem
                          :label="t('components.buttons.add_view')"
                          @click="toggleAddViewDialog"
                        >
                          <template #start>
                            <Icon class="fg-quaternary" name="plus" />
                          </template>
                        </DropdownItem>
                      </template>
                    </div>
                  </template>
                </Dropdown>
              </div>
              <Button
                v-if="customViews.length < 3 && allowViewCreation"
                variant="tertiary"
                type="gray"
                size="2xs"
                icon
                @click="toggleAddViewDialog"
              >
                <template #start>
                  <Icon name="plus" />
                </template>
              </Button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col gap-3 w-full mt-2">
            <div class="flex flex-col gap-2 w-full">
              <Divider
                v-if="allowViewCreation || customViews.length > 0"
                orientation="horizontal"
              />
              <div
                v-for="view in customViews"
                :key="view.id"
                :class="{
                  'bg-utility-brand-50_alt fg-brand-primary_alt':
                    isActive(view),
                  'hover:border-secondary text-quaternary': !loading,
                  'fg-quinary': loading,
                }"
                class="flex gap-3 cursor-pointer items-center rounded-lg shrink-0 transition-colors font-medium select-none focus-visible:outline-none border border-transparent justify-center px-4 py-1.5 text-base"
              >
                <button
                  :disabled="loading"
                  @click="handleSelectCustomView(view)"
                  class="grow text-left"
                >
                  {{ view.name }}
                </button>
                <Button
                  variant="tertiary"
                  type="gray"
                  size="sm"
                  icon
                  :disabled="loading"
                  @click="toggleSubDialogDialog(view.id)"
                >
                  <template #start>
                    <Icon name="dots-horizontal" />
                  </template>
                </Button>
              </div>
            </div>
            <Button
              v-if="allowViewCreation"
              :label="t('components.buttons.add_view')"
              @click="toggleAddViewDialog"
              variant="secondary"
              type="gray"
              size="xl"
            >
              <template #start>
                <Icon name="plus" />
              </template>
            </Button>
          </div>
        </template>
      </template>
    </Tabs>

    <Delete
      :open="isDeleteViewDialogOpen"
      @update:open="toggleDeleteViewDialog"
      @cancel="toggleDeleteViewDialog"
      @submit="handleDelete"
    />

    <Rename
      :open="isRenameViewDialogOpen"
      :initialState="
        views.filter((view) => view.id === selectedViewId)[0]?.name
      "
      :errorMessage="errorMessage"
      @update:name="name = $event"
      @update:open="toggleRenameViewDialog"
      @cancel="toggleRenameViewDialog"
      @submit="handleRename"
    />

    <Add
      :open="isAddViewDialogOpen"
      :name="name"
      :errorMessage="errorMessage"
      @update:name="name = $event"
      @update:open="toggleAddViewDialog"
      @cancel="toggleAddViewDialog"
      @submit="handleAdd"
    />

    <SubActions
      :open="isSubActionsDialogOpen"
      @openRenameDialog="openRenameViewDialog($event)"
      @openDeleteDialog="openDeleteViewDialog($event)"
    />
  </div>
</template>

<script setup>
// Import Vue Utilities
import { computed, ref } from "vue";

// Import Utilities
import { isEmpty } from "lodash";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";

// Import Components
import { Tabs, Tab } from "@/SharedComponents/Tabs";
import { Dropdown, DropdownItem } from "@/SharedComponents/Dropdown";
import { Divider } from "@/SharedComponents/Divider";
import { Icon } from "@/SharedComponents/Icon";
import { Button } from "@/SharedComponents/Buttons";
import {
  Rename,
  Delete,
  Add,
  SubActions,
} from "@/SharedComponents/PageHeader/Partials/CustomViewDialogs";
import { toast } from "@/SharedComponents/Toaster";
import {
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "radix-vue";

// Import Composables
import {
  useAddCustomFilesView,
  useDeleteCustomFilesView,
  useRenameCustomFilesView,
} from "@/Composables/useFilesViews";
import {
  useAddCustomTasksView,
  useDeleteCustomTasksView,
  useRenameCustomTasksView,
} from "@/Composables/useTasksViews";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useViewControls } from "@/Composables/useViewControls";
import { useFilters } from "@/Composables/useFilters";

// View Controls: Global
const viewControlsStore = useViewControls("viewControls");
const { loading } = storeToRefs(viewControlsStore);

// Define Props
const props = defineProps({
  currentView: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  views: {
    type: Array,
    required: true,
  },
  allowViewCreation: {
    type: Boolean,
    required: false,
    default: true,
  },
});

// Translations
const { t } = useI18n();

// Emits
const emit = defineEmits([
  "select-view",
  "delete-view",
  "add-view",
  "rename-view",
]);

// Breakpoints
const { maxSmall } = useBreakpoints();

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Refs
const isDropdownOpen = ref(false);
const isAddViewDialogOpen = ref(false);
const isDeleteViewDialogOpen = ref(false);
const isRenameViewDialogOpen = ref(false);
const isSubActionsDialogOpen = ref(false);
const selectedViewId = ref(null);
const name = ref("");

// Computed props
const activeTab = computed(() => {
  return props.views.findIndex((view) => view.id === props.currentView.id);
});

const defaultViews = computed(() => {
  return props.views.filter((item) => item.default);
});

const customViews = computed(() => {
  return props.views.filter((item) => !item.default);
});

const errorMessage = computed(() => {
  let views = props.views.filter(
    (view) => !selectedViewId.value || view.id !== selectedViewId.value
  );

  if (
    views.some((view) => view.name.toLowerCase() === name.value.toLowerCase())
  ) {
    return t("admin.views.errors.duplicate");
  }

  return "";
});

// Functions
const isActive = (view) => {
  return (
    activeTab.value === props.views.findIndex((items) => items.id === view.id)
  );
};

const handleSelectView = (index) => {
  emit("select-view", props.views[index]);
};

const handleSelectCustomView = (view) => {
  emit("select-view", view);
  isDropdownOpen.value = false;
};
const openRenameViewDialog = () => {
  isSubActionsDialogOpen.value = false;
  isRenameViewDialogOpen.value = true;
};

const openDeleteViewDialog = () => {
  isSubActionsDialogOpen.value = false;
  isDeleteViewDialogOpen.value = true;
};

const toggleSubDialogDialog = (id) => {
  if (isSubActionsDialogOpen.value) {
    isSubActionsDialogOpen.value = false;
    selectedViewId.value = null;
  } else {
    isSubActionsDialogOpen.value = true;
    selectedViewId.value = id;
  }
};

const toggleDeleteViewDialog = (id) => {
  if (isDeleteViewDialogOpen.value) {
    isDeleteViewDialogOpen.value = false;
    selectedViewId.value = null;
  } else {
    isDeleteViewDialogOpen.value = true;
    selectedViewId.value = id;
  }
};

const toggleRenameViewDialog = (id) => {
  isSubActionsDialogOpen.value = false;

  if (isRenameViewDialogOpen.value) {
    isRenameViewDialogOpen.value = false;
    name.value = "";
  } else {
    isRenameViewDialogOpen.value = true;
    selectedViewId.value = id;
  }
};

const toggleAddViewDialog = () => {
  if (isAddViewDialogOpen.value) {
    isAddViewDialogOpen.value = false;
    name.value = "";
  } else {
    isAddViewDialogOpen.value = true;
  }
};

const { createCustomTasksView } = useAddCustomTasksView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.create.success"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";

    // Emit add view
    emit("add-view", view);
  },
  onError: () => {
    // Show the error toast
    toast.success(t("toasts.views.create.error"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";
  },
});

const { createCustomFilesView } = useAddCustomFilesView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.create.success"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";

    // Emit add view
    emit("add-view", view);
  },
  onError: () => {
    // Show the error toast
    toast.success(t("toasts.views.create.error"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";
  },
});

const { deleteCustomTasksView } = useDeleteCustomTasksView({
  onSuccess: () => {
    // Show the success toast
    toast.success(t("toasts.views.delete.success"));

    // Navigate to the default view
    emit("select-view", props.views[0]);
    emit("delete-view", selectedViewId.value);

    // Close the dialog and reset the selectedViewId
    isDeleteViewDialogOpen.value = false;
    selectedViewId.value = null;
  },
  onError: () => {
    // Show the error toast
    toast.error(t("toasts.views.delete.error"));

    // Close the dialog and reset the selectedViewId
    isDeleteViewDialogOpen.value = false;
    selectedViewId.value = null;
  },
});

const { deleteCustomFilesView } = useDeleteCustomFilesView({
  onSuccess: () => {
    // Show the success toast
    toast.success(t("toasts.views.delete.success"));

    // Navigate to the default view
    emit("select-view", props.views[0]);
    emit("delete-view", selectedViewId.value);

    // Close the dialog and reset the selectedViewId
    isDeleteViewDialogOpen.value = false;
    selectedViewId.value = null;
  },
  onError: () => {
    // Show the error toast
    toast.error(t("toasts.views.delete.error"));

    // Close the dialog and reset the selectedViewId
    isDeleteViewDialogOpen.value = false;
    selectedViewId.value = null;
  },
});

const { renameCustomTasksView } = useRenameCustomTasksView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.update.success"));

    // Navigate to the default view
    emit("rename-view", view);

    // Close the dialog and reset the selectedViewId and name
    isRenameViewDialogOpen.value = false;
    name.value = "";
  },
  onError: (err) => {
    // Show the error toast
    toast.error(err.response.data.message || t("toasts.views.update.error"));

    // Close the dialog and reset the selectedViewId and name
    isRenameViewDialogOpen.value = false;
    name.value = "";
  },
});

const { renameCustomFilesView } = useRenameCustomFilesView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.update.success"));

    // Navigate to the default view
    emit("rename-view", view);

    // Close the dialog and reset the selectedViewId and name
    isRenameViewDialogOpen.value = false;
    name.value = "";
  },
  onError: (err) => {
    // Show the error toast
    toast.error(err.response.data.message || t("toasts.views.update.error"));

    // Close the dialog and reset the selectedViewId and name
    isRenameViewDialogOpen.value = false;
    name.value = "";
  },
});

const handleAdd = () => {
  const tenantId = globalFilterValues.value.tenant?.[0];

  if (props.type === "tasks") {
    createCustomTasksView({
      name: name.value,
      tenantId: tenantId,
      filterStates: {},
    });
  }

  if (props.type === "files") {
    createCustomFilesView({
      name: name.value,
      tenantId: tenantId,
      filterStates: {},
    });
  }
};

const handleDelete = () => {
  if (props.type === "tasks") {
    deleteCustomTasksView(selectedViewId.value);
  }

  if (props.type === "files") {
    deleteCustomFilesView(selectedViewId.value);
  }
};

const handleRename = () => {
  if (props.type === "tasks") {
    renameCustomTasksView({
      id: selectedViewId.value,
      name: name.value,
    });
  }

  if (props.type === "files") {
    renameCustomFilesView({
      id: selectedViewId.value,
      name: name.value,
    });
  }
};
</script>
